import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import Button from '../components/Button';
import FormField, { Input } from '../components/FormField';

function extractErrorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join(', ');
  return fallback;
}

function RequestForm() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/password-reset', { email });
      setSent(true);
    } catch {
      setError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="card text-center">
        <div className="text-4xl mb-3">📬</div>
        <h2 className="text-lg font-semibold text-ocean-900 mb-2">E-Mail gesendet</h2>
        <p className="text-sm text-slate-500 mb-4">
          Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Reset-Link geschickt.
          Schau auch im Spam-Ordner.
        </p>
        <Link to="/login" className="text-sm font-semibold text-ocean-600 hover:text-ocean-800 no-underline">
          Zurück zum Login
        </Link>
      </div>
    );
  }

  return (
    <div className="card">
      {error && <div className="alert-error mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <FormField label="E-Mail-Adresse" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de"
            autoComplete="email"
            required
          />
        </FormField>
        <Button type="submit" loading={loading} className="w-full mt-2">
          Reset-Link senden
        </Button>
      </form>
      <p className="text-center mt-4 text-sm text-slate-500">
        <Link to="/login" className="font-semibold text-ocean-600 hover:text-ocean-800 no-underline">
          Zurück zum Login
        </Link>
      </p>
    </div>
  );
}

function ConfirmForm({ token }) {
  const [form, setForm] = useState({ new_password: '', confirm: '' });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password !== form.confirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/password-reset/confirm', { token, new_password: form.new_password });
      setDone(true);
    } catch (err) {
      setError(extractErrorMessage(err, 'Fehler beim Zurücksetzen. Der Link könnte abgelaufen sein.'));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="card text-center">
        <div className="text-4xl mb-3">✅</div>
        <h2 className="text-lg font-semibold text-ocean-900 mb-2">Passwort geändert!</h2>
        <p className="text-sm text-slate-500 mb-4">Du kannst dich jetzt mit deinem neuen Passwort anmelden.</p>
        <Link to="/login" className="btn btn-primary text-sm">
          Zum Login
        </Link>
      </div>
    );
  }

  return (
    <div className="card">
      {error && <div className="alert-error mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <FormField label="Neues Passwort" required>
          <Input
            type="password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            placeholder="Mindestens 8 Zeichen"
            autoComplete="new-password"
            required
          />
        </FormField>
        <FormField label="Passwort bestätigen" required>
          <Input
            type="password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            placeholder="••••••••"
            autoComplete="new-password"
            required
          />
        </FormField>
        <Button type="submit" loading={loading} className="w-full mt-2">
          Passwort setzen
        </Button>
      </form>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');

  return (
    <div className="max-w-md mx-auto mt-4 sm:mt-8">
      <div className="text-center mb-8">
        <div className="text-4xl mb-2">🔑</div>
        <h1 className="text-2xl font-bold text-ocean-900">
          {token ? 'Neues Passwort setzen' : 'Passwort vergessen'}
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          {token
            ? 'Gib dein neues Passwort ein.'
            : 'Wir schicken dir einen Link per E-Mail.'}
        </p>
      </div>
      {token ? <ConfirmForm token={token} /> : <RequestForm />}
    </div>
  );
}
